import type {
  ModStatusBulkOut,
  ModStatusRefusalOut,
} from '@/api/generated/schemas';
import type { ApiErrorDetailItem } from '@/utils/apiError';

/**
 * Most objects one bulk moderation request may carry — the backend's
 * `MOD_STATUS_BULK_LIMIT`: `ids` (and publication `items`) longer than this
 * are a 422.
 */
export const MOD_STATUS_BULK_LIMIT = 50;

/**
 * The list of the request body a refusal points into: `ids` for the plain
 * `{ids, mod_status}` body, `items` for publications.
 */
export type BulkField = 'ids' | 'items';

/** One refusal reason and the labels of the objects it hit. */
export interface ItemErrorGroup {
  message: string;
  items: string[];
}

/** Request element a refusal points at — `["body", field, N]` → `N`. */
export const refusalIndex = (
  loc: ApiErrorDetailItem['loc'],
  field: BulkField
): number | undefined => {
  const [, at, index] = loc ?? [];
  return at === field && typeof index === 'number' ? index : undefined;
};

/**
 * Refusals grouped by reason. The backend reports one entry per broken rule
 * located at the request element; a batch usually fails for one or two
 * reasons, so names under each reason read far better than one line per
 * entry. `labelAt` returning `undefined` drops the entry (an index outside
 * the request).
 */
export const groupRefusals = (
  refusals: ApiErrorDetailItem[],
  field: BulkField,
  labelAt: (index: number, refusal: ApiErrorDetailItem) => string | undefined
): ItemErrorGroup[] => {
  const groups = new Map<string, string[]>();
  for (const refusal of refusals) {
    const index = refusalIndex(refusal.loc, field);
    if (index === undefined || !refusal.msg) continue;
    const label = labelAt(index, refusal);
    if (label === undefined) continue;
    groups.set(refusal.msg, [...(groups.get(refusal.msg) ?? []), label]);
  }
  return [...groups].map(([message, items]) => ({ message, items }));
};

/** Ids of a `{ids, mod_status}` request the backend refused — each once. */
export const refusedIds = (
  ids: number[],
  refusals: ApiErrorDetailItem[]
): number[] => [
  ...new Set(
    refusals
      .map((r) => refusalIndex(r.loc, 'ids'))
      .filter((index): index is number => index !== undefined)
      .map((index) => ids[index])
      .filter((id): id is number => id !== undefined)
  ),
];

/** Refusals of `ids`, grouped by reason and named by `labelOf` (or `#id`). */
export const groupIdRefusals = (
  ids: number[],
  refusals: ApiErrorDetailItem[],
  labelOf?: (id: number) => string | null | undefined
): ItemErrorGroup[] =>
  groupRefusals(refusals, 'ids', (index) => {
    const id = ids[index];
    return id === undefined ? undefined : (labelOf?.(id) ?? `#${id}`);
  });

/**
 * Sends `entries` in requests of at most `size`, one after another, and
 * merges the outcomes as if it had been one request: each refusal's index
 * is shifted by the offset of its chunk, so it still points into `entries`.
 * A failing chunk rejects; the chunks before it are already saved.
 */
export const sendInChunks = async <T>(
  entries: T[],
  field: BulkField,
  send: (chunk: T[]) => Promise<ModStatusBulkOut>,
  size = MOD_STATUS_BULK_LIMIT
): Promise<ModStatusBulkOut> => {
  const updated: number[] = [];
  const refused: ModStatusRefusalOut[] = [];
  for (let offset = 0; offset < entries.length; offset += size) {
    const result = await send(entries.slice(offset, offset + size));
    updated.push(...result.updated);
    for (const refusal of result.refused) {
      const index = refusalIndex(refusal.loc, field);
      refused.push(
        index === undefined
          ? refusal
          : {
              ...refusal,
              loc: [
                ...refusal.loc.slice(0, 1),
                field,
                index + offset,
                ...refusal.loc.slice(3),
              ],
            }
      );
    }
  }
  return { updated, refused };
};
